import { useState } from "react";
import { Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PortalBadge } from "@/components/PortalBadge";
import {
  useCia,
  useAddCia,
  useUpdateCia,
  useDeleteCia,
  type StockCia,
} from "@/api/stockSubresources";

interface Props {
  sku: string;
}

const PORTAL_OPTIONS = [
  "ebay",
  "amazon",
  "shopify",
  "drwebshop",
  "kaufland",
  "yatego",
  "hood",
  "fnac",
  "epages",
  "hitmeister",
  "event",
  "metro",
] as const;

export function ChannelsTab({ sku }: Props) {
  const { data: rows = [], isLoading } = useCia(sku);
  const addOne = useAddCia(sku);
  const updOne = useUpdateCia(sku);
  const delOne = useDeleteCia(sku);

  const [showNew, setShowNew] = useState(false);
  const [newPortal, setNewPortal] = useState<string>(PORTAL_OPTIONS[0]);
  const [newMax, setNewMax] = useState<string>("");

  async function onAdd() {
    if (!newPortal) return;
    if (rows.some((r) => r.portal === newPortal)) {
      toast.error(`Portal '${newPortal}' ist bereits aktiviert`);
      return;
    }
    try {
      await addOne.mutateAsync({
        portal: newPortal,
        is_active: false,
        max_quantity: newMax ? Number(newMax) : null,
      });
      setShowNew(false);
      setNewMax("");
      toast.success("Kanal aktiviert");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    }
  }

  async function onDelete(id: number) {
    try {
      await delOne.mutateAsync(id);
      toast.success("Kanal entfernt");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Loeschen fehlgeschlagen");
    }
  }

  async function onToggle(row: StockCia) {
    try {
      await updOne.mutateAsync({ id: row.id, payload: { is_active: !row.is_active } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update fehlgeschlagen");
    }
  }

  async function onSaveMax(row: StockCia, max: number | null) {
    try {
      await updOne.mutateAsync({ id: row.id, payload: { max_quantity: max } });
      toast.success("Max-Menge gespeichert");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update fehlgeschlagen");
    }
  }

  return (
    <Panel
      title="Kanal-Aktivierung"
      subtitle="Pro Marktplatz / Portal: aktiv, Höchstmenge"
      actions={
        !showNew && (
          <Button size="sm" onClick={() => setShowNew(true)}>
            <Plus className="size-3.5" />
            Kanal hinzufuegen
          </Button>
        )
      }
    >
      {showNew && (
        <div className="border border-primary/40 rounded-md p-4 mb-4 flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">
              Portal
            </div>
            <select
              value={newPortal}
              onChange={(e) => setNewPortal(e.target.value)}
              className="h-9 px-2 text-sm border border-line rounded bg-bg"
            >
              {PORTAL_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted font-semibold mb-1">
              Max. Menge (leer = unbegrenzt)
            </div>
            <Input
              type="number"
              value={newMax}
              onChange={(e) => setNewMax(e.target.value)}
              className="w-32 h-9 text-sm"
              placeholder="—"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={onAdd} disabled={addOne.isPending}>
              <Save className="size-3.5" />
              Anlegen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowNew(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted">Lade Kanaele…</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
              <th className="pb-2">Portal</th>
              <th className="pb-2">Status</th>
              <th className="pb-2 text-right">Max. Menge</th>
              <th className="pb-2">Accounts</th>
              <th className="pb-2">Targets</th>
              <th className="pb-2 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-2">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-6">
                  Keine Kanaele aktiviert.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <ChannelRow
                key={r.id}
                row={r}
                onToggle={() => onToggle(r)}
                onSaveMax={(v) => onSaveMax(r, v)}
                onDelete={() => onDelete(r.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function ChannelRow({
  row,
  onToggle,
  onSaveMax,
  onDelete,
}: {
  row: StockCia;
  onToggle: () => void;
  onSaveMax: (max: number | null) => Promise<void> | void;
  onDelete: () => void;
}) {
  const [editMax, setEditMax] = useState(false);
  const [maxDraft, setMaxDraft] = useState<string>(
    row.max_quantity != null ? String(row.max_quantity) : "",
  );

  async function commit() {
    const v = maxDraft.trim();
    const parsed = v === "" ? null : Number(v);
    if (parsed != null && Number.isNaN(parsed)) {
      toast.error("Ungueltige Zahl");
      return;
    }
    await onSaveMax(parsed);
    setEditMax(false);
  }

  return (
    <tr className="hover:bg-panel-2/40">
      <td className="py-2">
        <PortalBadge portal={row.portal === "drwebshop" ? "shopify" : row.portal} />
      </td>
      <td className="py-2">
        <button
          type="button"
          onClick={onToggle}
          className={`px-2 py-0.5 rounded text-xs font-semibold ${
            row.is_active
              ? "bg-success/20 text-success"
              : "bg-panel-2 text-muted"
          }`}
        >
          {row.is_active ? "Aktiv" : "Inaktiv"}
        </button>
      </td>
      <td className="py-2 text-right">
        {editMax ? (
          <div className="inline-flex items-center gap-1">
            <Input
              type="number"
              value={maxDraft}
              onChange={(e) => setMaxDraft(e.target.value)}
              className="w-24 h-7 text-xs text-right"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") void commit() }}
            />
            <Button size="sm" variant="ghost" onClick={commit} className="h-7">
              <Save className="size-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditMax(false)} className="h-7">
              <X className="size-3" />
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditMax(true)}
            className="font-mono text-sm hover:text-primary"
          >
            {row.max_quantity ?? <span className="text-muted-2">unbegrenzt</span>}
          </button>
        )}
      </td>
      <td className="py-2 font-mono text-xs">
        {row.accounts.length > 0 ? row.accounts.join(", ") : <span className="text-muted-2">—</span>}
      </td>
      <td className="py-2 font-mono text-xs">
        {row.targets.length > 0 ? row.targets.join(", ") : <span className="text-muted-2">—</span>}
      </td>
      <td className="py-2 text-right">
        <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 text-coral hover:text-coral">
          <Trash2 className="size-3" />
        </Button>
      </td>
    </tr>
  );
}
